import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Download } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, PROFILE_PDF } from "../data";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const linkCls = ({ isActive }) =>
    `font-heading text-[13px] font-medium tracking-wide transition-colors duration-300 relative group ${
      isActive ? "text-ink" : "text-ink/60 hover:text-ink"
    }`;

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled || open ? "bg-surface/90 backdrop-blur-2xl border-b border-ink/10" : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <nav className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-btn" className="text-ink shrink-0"><Logo /></Link>

        <div className="hidden xl:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkCls} data-testid={`nav-link-${l.to.slice(1)}`}>
              {({ isActive }) => (
                <>
                  {l.label}
                  <span className={`absolute -bottom-1 left-0 h-px bg-brand-green transition-all duration-300 ${isActive ? "w-full" : "w-0 group-hover:w-full"}`} />
                </>
              )}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            href={PROFILE_PDF}
            download
            data-testid="nav-download-profile"
            className="hidden md:inline-flex items-center gap-2 font-heading text-[12px] font-semibold tracking-wide border border-ink/20 text-ink/80 px-4 py-3 hover:bg-ink hover:text-white transition-colors duration-300"
          >
            <Download size={14} /> Profile
          </a>
          <NavLink
            to="/contact"
            data-testid="nav-enquire-btn"
            className="hidden sm:inline-flex font-heading text-[13px] font-semibold tracking-wide bg-brand-green text-white px-6 py-3 hover:bg-ink hover:text-white transition-colors duration-300"
          >
            Enquire
          </NavLink>
          <button className="xl:hidden text-ink p-2" onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="xl:hidden overflow-hidden bg-surface border-b border-ink/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {[{ label: "Home", to: "/" }, ...NAV_LINKS, { label: "Contact", to: "/contact" }].map((l) => (
                <NavLink key={l.to} to={l.to} className="text-left font-heading text-lg text-ink/80 py-3 border-b border-ink/5" data-testid={`nav-mobile-link-${l.to.slice(1) || "home"}`}>
                  {l.label}
                </NavLink>
              ))}
              <a href={PROFILE_PDF} download className="mt-4 inline-flex items-center gap-2 font-heading text-sm font-semibold bg-brand-green text-white px-6 py-3 w-max" data-testid="nav-mobile-download">
                <Download size={16} /> Download Profile
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
